import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  useMediaQuery,
  LinearProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import imageCompression from 'browser-image-compression';

interface MediaItem {
  id: string;
  filename: string;
  original_name: string;
  file_type: string | null;
  file_size: number;
  storage_url: string;
  is_approved: boolean;
  uploaded_at: string;
  guest_name?: string;
  guest_id: string;
}

export const GuestMediaGallery: React.FC = () => {
  const [myMedia, setMyMedia] = useState<MediaItem[]>([]);
  const [approvedMedia, setApprovedMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'my' | 'all'>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadMediaData();
  }, []);

  const loadMediaData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

      // Get current user's profile to get guest_id
      const profileResponse = await fetch(`${API_BASE_URL}/guests/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let currentGuestId: string | null = null;
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        currentGuestId = profileData.data?.id;
      }

      const response = await fetch(`${API_BASE_URL}/media`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load media');
      }

      const result = await response.json();
      const allMedia = result.data || [];
      
      console.log('Guest Media Debug:', {
        currentGuestId,
        totalMedia: allMedia.length,
        sampleMedia: allMedia[0],
        myMediaCount: allMedia.filter((m: any) => (m.guest_id || m.guestId) === currentGuestId).length,
        approvedCount: allMedia.filter((m: any) => m.is_approved || m.isApproved).length,
        allGuestIds: allMedia.map((m: any) => m.guest_id || m.guestId)
      });
      
      // Filter my media and approved media - handle both camelCase and snake_case
      // Sort by uploaded_at descending (newest first)
      const sortByDate = (a: any, b: any) => {
        const dateA = new Date(a.uploaded_at || a.uploadedAt).getTime();
        const dateB = new Date(b.uploaded_at || b.uploadedAt).getTime();
        return dateB - dateA; // Newest first
      };
      
      setMyMedia(
        allMedia
          .filter((m: any) => (m.guest_id || m.guestId) === currentGuestId)
          .sort(sortByDate)
      );
      setApprovedMedia(
        allMedia
          .filter((m: any) => m.is_approved || m.isApproved)
          .sort(sortByDate)
      );

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    // Process files with compression
    for (const file of Array.from(files)) {
      // Validate file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        errors.push(`${file.name}: Not an image or video file`);
        continue;
      }

      // Videos: just validate size (no compression)
      if (file.type.startsWith('video/')) {
        if (file.size > 50 * 1024 * 1024) {
          errors.push(`${file.name}: Video size exceeds 50MB`);
          continue;
        }
        validFiles.push(file);
        continue;
      }

      // Images: compress if needed
      try {
        let processedFile = file;
        
        // Compress images larger than 5MB to ensure they stay under API Gateway limits
        if (file.size > 5 * 1024 * 1024) {
          const options = {
            maxSizeMB: 5, // Target max size
            maxWidthOrHeight: 1920, // Max dimension
            useWebWorker: true,
            fileType: file.type as any
          };
          
          processedFile = await imageCompression(file, options);
          console.log(`Compressed ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
        }
        
        // Final size check
        if (processedFile.size > 50 * 1024 * 1024) {
          errors.push(`${file.name}: File too large even after compression`);
          continue;
        }
        
        validFiles.push(processedFile);
      } catch (err) {
        console.error('Compression error:', err);
        errors.push(`${file.name}: Failed to process image`);
      }
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setUploadDialogOpen(true);
    }

    // Reset input
    event.target.value = '';
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploadLoading(true);
    setError(null);
    setUploadProgress({});

    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

      // Upload files one by one to track progress
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        const formData = new FormData();
        formData.append('media', file);

        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const response = await fetch(`${API_BASE_URL}/media/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`${file.name}: ${errorData.error || 'Failed to upload'}`);
        }

        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      }

      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadProgress({});
      loadMediaData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload media');
    } finally {
      setUploadLoading(false);
    }
  };

  const handlePreviewMedia = (index: number) => {
    setSelectedMediaIndex(index);
    setPreviewDialogOpen(true);
  };

  const handleNextMedia = () => {
    const displayMedia = viewMode === 'my' ? myMedia : approvedMedia;
    setSelectedMediaIndex((prev) => (prev + 1) % displayMedia.length);
  };

  const handlePrevMedia = () => {
    const displayMedia = viewMode === 'my' ? myMedia : approvedMedia;
    setSelectedMediaIndex((prev) => (prev - 1 + displayMedia.length) % displayMedia.length);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      handleNextMedia();
    } else if (event.key === 'ArrowLeft') {
      handlePrevMedia();
    } else if (event.key === 'Escape') {
      setPreviewDialogOpen(false);
    }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setActionLoading(mediaId);
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

      const response = await fetch(`${API_BASE_URL}/media/${mediaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete media');
      }

      loadMediaData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete media');
    } finally {
      setActionLoading(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownloadMedia = async (media: MediaItem) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(media.storage_url);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = media.original_name || `photo-${media.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download media. Please try again.');
      console.error('Download error:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const displayMedia = viewMode === 'my' ? myMedia : approvedMedia;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Photo Gallery
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          component="label"
        >
          Upload Photos
          <input
            type="file"
            hidden
            multiple
            accept="image/*,video/*"
            onChange={handleFileSelect}
          />
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* View Mode Toggle */}
      <Box display="flex" gap={1} mb={3}>
        <Button
          variant={viewMode === 'all' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('all')}
        >
          All Approved ({approvedMedia.length})
        </Button>
        <Button
          variant={viewMode === 'my' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('my')}
        >
          My Uploads ({myMedia.length})
        </Button>
      </Box>

      {displayMedia.length === 0 ? (
        <Alert severity="info">
          {viewMode === 'my' 
            ? 'You haven\'t uploaded any photos yet. Click "Upload Photo" to share your memories!'
            : 'No approved photos yet. Be the first to share!'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {displayMedia.map((media) => (
            <Grid item xs={6} sm={4} md={3} key={media.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ position: 'relative', paddingTop: '100%', backgroundColor: '#f5f5f5' }}>
                  <CardMedia
                    component={media.file_type?.startsWith('video/') ? 'video' : 'img'}
                    image={media.storage_url}
                    title={media.original_name}
                    controls={media.file_type?.startsWith('video/')}
                    loading="lazy"
                    sx={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      backgroundColor: '#f5f5f5'
                    }}
                    onClick={() => handlePreviewMedia(displayMedia.indexOf(media))}
                  />
                  
                  {/* Download icon button - always visible */}
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      zIndex: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      }
                    }}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadMedia(media);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  
                  {/* Status badge for my uploads */}
                  {viewMode === 'my' && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
                      <Chip
                        icon={media.is_approved ? <CheckCircleIcon /> : <PendingIcon />}
                        label={media.is_approved ? 'Approved' : 'Pending'}
                        color={media.is_approved ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
                
                <CardContent sx={{ flexGrow: 1, p: 1 }}>
                  <Typography variant="caption" display="block" noWrap>
                    {media.guest_name || 'Guest'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(media.uploaded_at).toLocaleDateString()}
                  </Typography>
                  {viewMode === 'my' && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeleteMedia(media.id)}
                      disabled={actionLoading === media.id}
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Delete
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploadLoading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Photos ({selectedFiles.length} selected)
          <IconButton
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFiles([]);
              setUploadProgress({});
            }}
            disabled={uploadLoading}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedFiles.length > 0 && (
            <Box>
              <List dense>
                {selectedFiles.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                    />
                    {uploadProgress[file.name] !== undefined && (
                      <Box sx={{ width: '100%', ml: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={uploadProgress[file.name]} 
                        />
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Your photos will be uploaded and visible to all guests immediately.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFiles([]);
              setUploadProgress({});
            }} 
            disabled={uploadLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploadLoading}
            startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploadLoading ? `Uploading... (${Object.keys(uploadProgress).length}/${selectedFiles.length})` : `Upload ${selectedFiles.length} Photo${selectedFiles.length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog with Carousel */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        onKeyDown={handleKeyPress}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          Photo {selectedMediaIndex + 1} of {displayMedia.length}
          <IconButton
            onClick={() => setPreviewDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ position: 'relative', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {displayMedia[selectedMediaIndex] && (
            <Box sx={{ width: '100%', position: 'relative' }}>
              {/* Navigation Buttons */}
              {displayMedia.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevMedia}
                    sx={{
                      position: 'absolute',
                      left: -60,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      zIndex: 10
                    }}
                  >
                    <ArrowBackIcon fontSize="large" />
                  </IconButton>
                  <IconButton
                    onClick={handleNextMedia}
                    sx={{
                      position: 'absolute',
                      right: -60,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      zIndex: 10
                    }}
                  >
                    <ArrowForwardIcon fontSize="large" />
                  </IconButton>
                </>
              )}

              {/* Media Display */}
              {displayMedia[selectedMediaIndex].file_type?.startsWith('video/') ? (
                <video
                  src={displayMedia[selectedMediaIndex].storage_url}
                  controls
                  style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <img
                  src={displayMedia[selectedMediaIndex].storage_url}
                  alt={displayMedia[selectedMediaIndex].original_name}
                  loading="lazy"
                  style={{ 
                    width: '100%', 
                    maxHeight: '70vh', 
                    objectFit: 'contain',
                    cursor: displayMedia.length > 1 ? 'pointer' : 'default'
                  }}
                  onClick={displayMedia.length > 1 ? handleNextMedia : undefined}
                />
              )}
              
              {/* Media Info */}
              <Box mt={2} sx={{ color: 'white' }}>
                <Typography variant="body2">
                  <strong>Uploaded by:</strong> {displayMedia[selectedMediaIndex].guest_name || 'Guest'}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(displayMedia[selectedMediaIndex].uploaded_at).toLocaleString()}
                </Typography>
                {displayMedia[selectedMediaIndex].file_size && (
                  <Typography variant="body2">
                    <strong>Size:</strong> {formatFileSize(displayMedia[selectedMediaIndex].file_size)}
                  </Typography>
                )}
              </Box>

              {/* Swipe hint for mobile */}
              {isMobile && displayMedia.length > 1 && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                  Tap image or use arrows to navigate
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}>
          <Button onClick={() => setPreviewDialogOpen(false)} sx={{ color: 'white' }}>
            Close
          </Button>
          {displayMedia[selectedMediaIndex] && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadMedia(displayMedia[selectedMediaIndex])}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
